import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  Box,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { toast } from "react-toastify";
import { oauthClientService } from "../../utils/services/o-auth-client";
import { randomUid } from "../../utils/utility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { providerService } from "../../utils/services/providerService";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import { usersService } from "../../utils/services/usersService";

const OAuthClientModal = ({
  open,
  handleClose,
  onAddData,
  selectedAuth,
  user,
}) => {
  const [formData, setFormData] = useState({
    client_id: "",
    client_secret: "",
    redirect_uris: [""],
    grants: [],
    expires_at: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [noExpiration, setNoExpiration] = useState(true);

  useEffect(() => {
    if (selectedAuth) {
      const hasExpiration = selectedAuth.expires_at;
      setNoExpiration(!hasExpiration);
      setFormData({
        client_id: selectedAuth.client_id || "",
        client_secret: selectedAuth.client_secret || "",
        redirect_uris: Array.isArray(selectedAuth.redirect_uris)
          ? selectedAuth.redirect_uris
          : [""],
        grants: Array.isArray(selectedAuth.grants),
        user: selectedUser?.name,
        expires_at: hasExpiration
          ? new Date(selectedAuth.expires_at).toISOString().split("T")[0]
          : "",
      });
    } else {
      setFormData({
        client_id: "",
        client_secret: "",
        redirect_uris: [""],
        grants: [],
        expires_at: "",
      });
      setSelectedUser(null);
      setNoExpiration(true);
    }
  }, [selectedAuth]);

  const validateForm = () => {
    const errors = {};
    if (!formData.client_id.trim()) errors.client_id = "Client ID is required";
    if (!formData.client_secret.trim())
      errors.client_secret = "Client secret is required";
    if (formData.redirect_uris.some((uri) => !uri.trim()))
      errors.redirect_uris = "Redirect URIs cannot be empty";
    if (!formData.grants.length)
      errors.grants = "At least one grant must be selected";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      client_id: formData.client_id,
      client_secret: formData.client_secret,
      redirect_uris: formData.redirect_uris,
      grants: formData.grants,
      user: user ?? selectedUser?._id,
      expires_at: noExpiration ? null : formData.expires_at || null,
    };

    const action = selectedAuth
      ? oauthClientService.edit(selectedAuth._id, payload)
      : oauthClientService.create(payload);

    action
      .then(() => {
        toast.success(
          `Client ${selectedAuth ? "updated" : "created"} successfully!`,
        );
        onAddData && onAddData();
        onCancel();
      })
      .catch(() => {
        toast.error(`Failed to ${selectedAuth ? "update" : "create"} client.`);
      });
  };

  const onCancel = () => {
    setFormData({
      client_id: "",
      client_secret: "",
      redirect_uris: [""],
      grants: [],
      expires_at: "",
    });
    setNoExpiration(true);
    handleClose();
  };

  const handleAddUri = () => {
    setFormData((prev) => ({
      ...prev,
      redirect_uris: Array.isArray(prev.redirect_uris)
        ? [...prev.redirect_uris, ""]
        : [""],
    }));
  };

  const handleUriChange = (index, value) => {
    setFormData((prev) => {
      const uris = Array.isArray(prev.redirect_uris)
        ? [...prev.redirect_uris]
        : [""];
      uris[index] = value;
      return { ...prev, redirect_uris: uris };
    });
  };

  const handleRemoveUri = (index) => {
    setFormData((prev) => {
      const uris = [...prev.redirect_uris];
      uris.splice(index, 1);
      return {
        ...prev,
        redirect_uris: uris.length > 0 ? uris : [""],
      };
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DialogTitle>{"Create OAuth Client"}</DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider />
      <DialogContent>
        <Grid container spacing={1}>
          {/* Client ID */}
          <Grid item xs={12}>
            <Typography fontWeight={500}>
              Client ID <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={formData.client_id}
              onChange={(e) =>
                setFormData({ ...formData, client_id: e.target.value })
              }
              error={!!formErrors.client_id}
              helperText={formErrors.client_id}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography fontWeight={500}>
              Client Secret <span style={{ color: "red" }}>*</span>
            </Typography>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs>
                <TextField
                  fullWidth
                  size="small"
                  value={formData.client_secret}
                  onChange={(e) =>
                    setFormData({ ...formData, client_secret: e.target.value })
                  }
                  error={!!formErrors.client_secret}
                  helperText={formErrors.client_secret}
                  InputProps={{
                    endAdornment: (
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton
                          aria-label="auto generate"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              client_secret: randomUid(),
                            })
                          }
                          size="small"
                          title="Auto Generate Client Secret"
                        >
                          <AutorenewIcon fontSize="small" />
                        </IconButton>
                        {formData.client_secret && (
                          <IconButton
                            aria-label="copy"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                formData.client_secret,
                              );
                              toast.success(
                                "Client Secret copied to clipboard!",
                              );
                            }}
                            edge="end"
                            size="small"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="caption"
              color="warning.main"
              sx={{
                fontStyle: "italic",
                fontSize: "12px",
                display: "block",
                mt: 0.5,
              }}
            >
              ⚠️ Note: Please store the client secret securely for future use.
              You cannot retrieve it once it is stored.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography fontWeight={500}>Expiration Date</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={noExpiration}
                    onChange={(e) => {
                      setNoExpiration(e.target.checked);
                      if (e.target.checked) {
                        setFormData({ ...formData, expires_at: "" });
                      }
                    }}
                    size="small"
                  />
                }
                label="No expiration"
                sx={{ margin: 0 }}
              />
            </Box>
            {!noExpiration && (
              <TextField
                fullWidth
                size="small"
                type="date"
                value={formData.expires_at}
                onChange={(e) =>
                  setFormData({ ...formData, expires_at: e.target.value })
                }
                helperText="Set when this OAuth client should expire"
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ mt: 1 }}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography fontWeight={500}>
              {`Redirect URL(s)`} <span style={{ color: "red" }}>*</span>
            </Typography>

            {formData.redirect_uris.map((uri, index) => (
              <Grid container spacing={1} alignItems="center" key={index}>
                <Grid item xs>
                  <TextField
                    fullWidth
                    size="small"
                    value={uri}
                    onChange={(e) => handleUriChange(index, e.target.value)}
                    error={!!formErrors.redirect_uris && !uri.trim()}
                    helperText={
                      !!formErrors.redirect_uris && !uri.trim()
                        ? formErrors.redirect_uris
                        : ""
                    }
                    sx={{ mb: 1 }}
                  />
                </Grid>

                <Grid item>
                  {index === formData.redirect_uris.length - 1 ? (
                    <IconButton onClick={handleAddUri} title="Add more URL">
                      <AddIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      onClick={() => handleRemoveUri(index)}
                      title="Remove URL"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            ))}
          </Grid>

          {/* Grants */}
          <Grid item xs={12}>
            <Typography fontWeight={500}>
              Grants <span style={{ color: "red" }}>*</span>
            </Typography>
            <Select
              fullWidth
              size="small"
              multiple
              value={Array.isArray(formData.grants) ? formData.grants : []}
              onChange={(e) =>
                setFormData({ ...formData, grants: e.target.value })
              }
              renderValue={(selected) => selected.join(", ")}
              error={!!formErrors.grants}
            >
              {["authorization_code", "refresh_token"].map((grant) => (
                <MenuItem key={grant} value={grant}>
                  <Checkbox checked={formData?.grants?.includes(grant)} />
                  <ListItemText primary={grant} />
                </MenuItem>
              ))}
            </Select>
            {formErrors.grants && (
              <Typography color="error" variant="body2">
                {formErrors.grants}
              </Typography>
            )}
          </Grid>
          {user ? (
            <></>
          ) : (
            <Grid item xs={12}>
              <Typography fontWeight={500}>
                User <span style={{ color: "red" }}>*</span>
              </Typography>
              <AutocompleteWithPagination
                hideLabel
                backgroundColor={"var(--bg-paper)"}
                customLabel={"User"}
                selectedItem={selectedUser}
                onDataChange={(e) => {
                  if (e) {
                    setSelectedUser(e);
                  } else {
                    setSelectedUser(null);
                  }
                }}
                width={"100%"}
                optionKey={(user) => `${user.name} (${user.email})`}
                apiEndPoint={(limit, skip, search, client) => {
                  console.log("Fetching users with params:", {
                    limit,
                    skip,
                    client,
                    search,
                  });

                  return usersService.fetch(limit, skip, client, search);
                }}
              ></AutocompleteWithPagination>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onCancel()} variant="outlined">
          Cancel
        </Button>

        <Button onClick={handleSave} variant="contained" color="primary">
          {selectedAuth ? "Save Changes" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OAuthClientModal;
